<div class="workspace-page">
  <div class="workspace-page__panel workspace-page__panel--card workspace-page__panel--dense">
    <header class="workspace-page__heading">
      <h1 class="workspace-page__title">Analysis</h1>
    </header>

  <!-- The same control the Board carries. These pages stopped being cards on
       the Dashboard, so without it the only way back to the leads you were
       looking at would be the browser button. It marks itself current from
       the URL, so nothing here has to say which tab this is. -->
  <app-leads-view-switcher class="rb-switcher"></app-leads-view-switcher>

  <mat-progress-bar mode="indeterminate" *ngIf="loading"></mat-progress-bar>
  <div class="br-alert br-alert--danger cc-error" *ngIf="loadError"><br-icon>error_outline</br-icon> {{ loadError }}</div>

  <ng-container *ngIf="summary && !loadError">
    <!-- ── What has been won ───────────────────────────────────────────── -->
    <div class="cc-kpis">
      <!-- ONE TILE ABOUT WINNING, not two.
           "Won this month" and "Win rate" were separate boxes asking the same
           question at two lengths, and read side by side they invited the
           wrong comparison - a good month next to a poor year looks like a
           contradiction rather than the two facts it is. Together the recent
           number leads and the longer run qualifies it. -->
      <div class="br-card cc-kpi cc-kpi--won" [matTooltip]="explain.won"
           matTooltipPosition="below">
        <div class="cc-kpi-num">{{ thisMonth.won }}</div>
        <div class="cc-kpi-label">Won this month</div>
        <div class="cc-kpi-note">
          <span class="cc-kpi-money" *ngIf="thisMonth.wonContribution">
            {{ thisMonth.wonContribution | currency:'USD':'symbol':'1.0-0' }} ·
          </span>
          {{ thisMonth.lost }} lost
        </div>
        <!-- The twelve-month reading, as context rather than as a rival
             headline. Withheld entirely while nothing has closed: 0% would be
             a claim about a company that has simply not finished anything. -->
        <div class="cc-kpi-note cc-kpi-rate" *ngIf="winRate !== null; else nothingClosed">
          {{ winRate | percent:'1.0-0' }} won of {{ wonTwelveMonths + lostTwelveMonths }}
          closed in 12 months<span *ngIf="averageWonDeal">
            · {{ averageWonDeal | currency:'USD':'symbol':'1.0-0' }} average</span>
        </div>
        <ng-template #nothingClosed>
          <div class="cc-kpi-note cc-kpi-rate">nothing closed in the last 12 months</div>
        </ng-template>
      </div>
    </div>

    <!-- ── What is rotting. Its own band, and quiet when there is nothing in
         it: three zeros side by side read as an alarm at a glance. ─────── -->
    <div class="br-card cc-attention" *ngIf="health">
      <span class="cc-attention-title">Needs attention</span>

      <ng-container *ngIf="needsAttention; else nothingWaiting">
      <span class="cc-att" [class.cc-att-on]="health.stalledCount > 0">
        <strong>{{ health.stalledCount }}</strong>
        stalled over {{ health.stalledAfterDays }} days<span
          class="cc-att-money" *ngIf="health.stalledValue">
          · {{ health.stalledValue | currency:'USD':'symbol':'1.0-0' }}</span>
      </span>

      <span class="cc-att" [class.cc-att-on]="health.overdueActionCount > 0">
        <strong>{{ health.overdueActionCount }}</strong>
        overdue next action{{ health.overdueActionCount === 1 ? '' : 's' }}
      </span>

      <span class="cc-att cc-att-link" [class.cc-att-on]="summary.pendingApprovals > 0"
            (click)="openApprovals()" tabindex="0" (keyup.enter)="openApprovals()">
        <strong>{{ summary.pendingApprovals }}</strong> pending approvals
        <br-icon class="cc-att-go">chevron_right</br-icon>
      </span>
      </ng-container>

      <ng-template #nothingWaiting>
        <span class="cc-att-clear">nothing stalled, overdue or waiting on a decision</span>
      </ng-template>
    </div>

    <div class="cc-grid">
      <!-- Funnel -->
      <section class="br-card cc-card cc-funnel">
        <!-- "Pipeline funnel" named a shape and a piece of jargon, and said
             nothing about what is in it. This is the company's open leads,
             counted by the stage each one has reached - which is also what
             clicking a row opens. -->
        <h3>Leads by stage</h3>
        <div class="cc-funnel-row" *ngFor="let slice of funnel"
             (click)="openBoard(slice.column)" tabindex="0" (keyup.enter)="openBoard(slice.column)">
          <span class="cc-funnel-label">{{ slice.label }}</span>
          <span class="cc-funnel-bar-wrap">
            <span class="cc-funnel-bar" [style.width]="funnelWidth(slice)"></span>
          </span>
          <span class="cc-funnel-count">{{ slice.count }}</span>
          <span class="cc-funnel-money" *ngIf="slice.contribution">
            {{ slice.contribution | currency:'USD':'symbol':'1.0-0' }}</span>
        </div>
        <p class="cc-empty" *ngIf="funnelEmpty">No open leads yet.</p>
      </section>

      <!-- Outcome trend -->
      <section class="br-card cc-card cc-trend">
        <h3>Won / Lost — last 12 months</h3>
        <div class="cc-trend-chart" *ngIf="hasOutcomes; else noTrend">
          <div class="cc-trend-col" *ngFor="let m of trendMonths">
            <div class="cc-trend-bars">
              <span class="cc-trend-won" [style.height]="trendHeight(m.won)" matTooltip="{{ m.won }} won"></span>
              <span class="cc-trend-lost" [style.height]="trendHeight(m.lost)" matTooltip="{{ m.lost }} lost"></span>
            </div>
            <span class="cc-trend-month">{{ monthLabel(m.month) }}</span>
          </div>
        </div>
        <ng-template #noTrend><p class="cc-empty">No closed leads in the last 12 months.</p></ng-template>
        <div class="cc-legend" *ngIf="hasOutcomes">
          <span><i class="cc-swatch cc-won"></i> Won</span>
          <span><i class="cc-swatch cc-lost"></i> Lost</span>
        </div>
      </section>

      <!-- Origins -->
      <section class="br-card cc-card cc-origins">
        <h3>Leads by origin</h3>
        <div class="cc-origin-row" *ngFor="let o of origins">
          <span class="cc-origin-label">{{ o.origin }}</span>
          <span class="cc-origin-bar-wrap"><span class="cc-origin-bar" [style.width]="originWidth(o)"></span></span>
          <span class="cc-origin-count">{{ o.count }}</span>
        </div>
        <p class="cc-empty" *ngIf="!origins.length">No leads yet.</p>
      </section>
    </div>
  </ng-container>

  </div>
</div>
